import axios from "axios";
import { setupCache } from "axios-cache-interceptor";
import qs from "qs";

/**
 * Get full Strapi URL from path
 * @param {string} path Path of the URL
 * @returns {string} Full Strapi URL
 */
export function getStrapiURL(path = "") {
  return `${process.env.BACKEND_URL || "http://localhost:1337"}${path}`;
}

const CACHE_TIME = 2 * 60 * 1000;

const isDev = process.env.NODE_ENV === "development";
const timeToLoad = isDev ? 0 : CACHE_TIME;

const api = setupCache(
  axios.create({
    baseURL: process.env.BACKEND_URL || "http://localhost:1337",
    headers: {
      "Content-Type": "application/json",
    },
  }),
  { ttl: timeToLoad }
);

export function clearApiCache() {
  const data = api.storage?.data;
  if (data) {
    Object.keys(data).forEach((key) => delete data[key]);
  }
}

/**
 * Helper to make GET requests to Strapi API endpoints
 * @param {string} path Path of the API route
 * @param {Object} urlParamsObject URL params object, will be stringified
 * @param {Object} options Options passed to fetch
 * @returns Parsed API call response
 */

export async function fetchAPI(path, urlParamsObject = {}, options = {}) {
  // Normalize cache option: v1.x accepts false or CacheOptions object (not true)
  const { cache, ...restOptions } = options;
  const mergedOptions = {
    ...restOptions,
    ...(cache === false ? { cache: false } : {}),
  };

  // Build request URL
  const queryString = qs.stringify(urlParamsObject);
  const requestUrl = `/api${path}${queryString ? `?${queryString}` : ""}`;

  // Trigger API call
  return api
    .get(requestUrl, { ...mergedOptions })
    .then((response) => response.data)
    .catch((err) => {
      // 404 is not an error, just return empty data
      if (err?.response?.status !== 404) {
        console.warn(
          `Request to ${err?.request?.path} failed with code ${err?.response?.status} and message "${err?.response?.statusText}"`
        );
      }

      return { data: [] };
    });
}

// Client-side cache value - set once on app initialization from server
let clientSideCacheEnabled = null;

/**
 * Set the cache enabled value on the client-side
 * This should be called once when the app initializes to avoid runtime API calls to /maintenance
 * @param {boolean} value - The cache enabled value from server-side
 */
export function setClientCacheEnabled(value) {
  clientSideCacheEnabled = value;
}

// In-memory cache for maintenance check (server-side only)
// Cache for 30 seconds to avoid duplicate calls within same server request
let maintenanceCachedValue = null;
let maintenanceCachedTime = 0;
const MAINTENANCE_CACHE_TTL = 30000; // 30 seconds (matches backend cache)

export async function getIsCacheEnabled() {
  const isClient = typeof window !== "undefined";

  // On client-side: use the value from the store (no API call needed!)
  if (isClient && clientSideCacheEnabled !== null) {
    return clientSideCacheEnabled;
  }

  // On server-side: use in-memory cache to avoid duplicate calls
  const now = Date.now();

  // Return cached value if still valid (within 30 seconds)
  if (
    maintenanceCachedValue !== null &&
    now - maintenanceCachedTime < MAINTENANCE_CACHE_TTL
  ) {
    return maintenanceCachedValue;
  }

  // Fetch fresh value (server-side only)
  // NOTE: This should only be called ONCE per request from _app.jsx
  // All other helpers should read from appContext.cacheEnabled
  const maintenanceMode = await fetchAPI(
    "/maintenance",
    {
      fields: ["disableCache"],
    },
    { cache: false }
  );

  maintenanceCachedValue = !maintenanceMode.data?.disableCache;
  maintenanceCachedTime = now;

  return maintenanceCachedValue;
}
